import clsx from 'clsx';
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type KeyboardEvent as ReactKeyboardEvent,
  Children,
} from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { isElement } from 'react-is';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  _useAriaLabelingProps,
  _useSafeForwardProps as useSafeForwardProps,
  type AriaLabelingProps,
  type DataTestId,
  type MaskingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { Flex, Surface } from '@dynatrace/strato-components/layouts';
import Colors from '@dynatrace/strato-design-tokens/colors';

import { MicroguideContext } from './contexts/MicroguideContext.js';
import {
  microguideFooterCSS,
  microguideWrapperCSS,
  microguideWrapperPositionCSS,
} from './Microguide.sty.js';
import { Step } from './Step.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { useKeyboardFocusHandling } from '../hooks/useKeyboardFocusHandling.js';
import { Content } from '../shared-components/Content.js';
import { Title } from '../shared-components/Title.js';
import { Visual } from '../shared-components/Visual.js';

// #region Typings
/**
 * Accepted properties for Microguide
 * @public
 */
export interface MicroguideProps
  extends WithChildren,
    AriaLabelingProps,
    DataTestId,
    MaskingProps {
  /**
   * Whether the Microguide is currently shown or not. Has to be controlled by the consumer.
   */
  open: boolean;
  /**
   * Placement of the Microguide.
   * @defaultValue 'bottom-right'
   */
  placement?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  /**
   * Callback fired when closing the Microguide.
   * Indicates the current step (zero-based) when closing and whether the Microguide was finished (i.e. closed in the last step).
   * @param currentStep - The currently active step when closing.
   * @param finished - Whether the guide was closed in the last step.
   */
  onClose: (currentStep: number, finished: boolean) => void;
  /**
   * Callback fired when the user navigates between steps. Indicates the target step (zero-based).
   * @param step - The step that the user navigated to.
   */
  onStepChange?: (step: number) => void;
}
// #endregion Typings

/**
 * Microguide component
 */
const MicroGuideRoot = forwardRef<HTMLDivElement, MicroguideProps>(
  (props, forwardedRef) => {
    const {
      open,
      placement = 'bottom-right',
      onClose,
      children,
      onStepChange,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;
    const microguideContainer = useRef<HTMLElement | null>(null);
    const closeButtonRef = useRef<HTMLButtonElement | null>(null);
    const steps: ReactNode[] = [];
    const [currentStep, setCurrentStep] = useState(0);
    const [isNext, setIsNext] = useState(true);
    const [hasTitle, setHasTitle] = useState(false);
    const [hasContent, setHasContent] = useState(false);
    const [handleFocusOnKeydown, closedOnKeydown] =
      useKeyboardFocusHandling(closeButtonRef);
    const overlayStacking = useOverlayStacking(undefined, 'microguide');
    const intl = useIntl();
    const ariaLabelingProps = _useAriaLabelingProps(remainingProps);
    const forwardProps = useSafeForwardProps(remainingProps, ariaLabelingProps);

    Children.forEach(children, (child) => {
      if (isElement(child) && child.type === Step) {
        steps.push(child);
      } else {
        // eslint-disable-next-line no-console
        console.warn('Only Step is a valid child of the Microguide.');
      }
    });

    const isLastStep = currentStep + 1 >= steps.length;
    if (hasTitle && ariaLabelingProps['aria-labelledby'] === undefined) {
      ariaLabelingProps['aria-labelledby'] = 'strato-microguide-title';
    }
    if (hasContent && ariaLabelingProps['aria-describedby'] === undefined) {
      ariaLabelingProps['aria-describedby'] = 'strato-microguide-content';
    }

    // Handle the escape key
    const closeOnEscape = (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closedOnKeydown.current = true;
        onClose(currentStep, currentStep === steps.length);
      }
    };

    useLayoutEffect(() => {
      const toastContainer = document.querySelector('[data-toast-container]');
      microguideContainer.current = document.createElement('div');
      microguideContainer.current.setAttribute('aria-live', 'polite');
      microguideContainer.current.setAttribute(
        'data-microguide-container',
        'true',
      );

      if (toastContainer && toastContainer.parentNode) {
        toastContainer.parentNode.insertBefore(
          microguideContainer.current,
          toastContainer,
        );
      } else {
        document.body.appendChild(microguideContainer.current);
      }

      return () => {
        microguideContainer.current?.remove();
      };
    }, []);

    useEffect(() => {
      // Reset microguide to the first page once it closes
      if (open === false) {
        setCurrentStep(0);
        // handle focus if closed via keyboard
        handleFocusOnKeydown();
      }
    }, [handleFocusOnKeydown, open]);

    const handleBack = () => {
      const previousStep = currentStep - 1;
      setIsNext(false);
      onStepChange?.(previousStep);
      setCurrentStep(previousStep);
    };

    const handleNext = () => {
      const nextStep = currentStep + 1;
      setIsNext(true);
      onStepChange?.(nextStep);
      setCurrentStep(nextStep);
    };

    return open
      ? createPortal(
          <Surface
            className={clsx(
              microguideWrapperCSS,
              microguideWrapperPositionCSS[placement],
            )}
            style={{ zIndex: overlayStacking.style.zIndex }}
            elevation="floating"
            onKeyDown={closeOnEscape}
            role="dialog"
            aria-modal="false"
            ref={forwardedRef}
            data-testid={dataTestId}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
            {...forwardProps}
            {...ariaLabelingProps}
          >
            <MicroguideContext.Provider
              value={{
                onClose,
                currentStep,
                totalSteps: steps.length,
                isNext,
                setHasContent,
                setHasTitle,
                closeButtonRef,
                closedOnKeydown,
              }}
            >
              {steps[currentStep]}
            </MicroguideContext.Provider>
            <Flex
              className={microguideFooterCSS}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              gap={16}
              paddingX={16}
              paddingBottom={16}
              paddingTop={8}
            >
              <div style={{ color: Colors.Text.Neutral.Subdued }}>
                {steps.length > 1 && `${currentStep + 1}/${steps.length}`}
              </div>
              <Flex gap={8}>
                {currentStep > 0 && (
                  <Button onClick={handleBack}>
                    <FormattedMessage
                      description="Microguide Back button."
                      defaultMessage="Back"
                      id="N27hQ6LyGqdym2Yi"
                    />
                  </Button>
                )}
                <Button
                  variant={isLastStep ? 'accent' : 'emphasized'}
                  color={isLastStep ? 'primary' : 'neutral'}
                  onClick={
                    isLastStep ? () => onClose(currentStep, true) : handleNext
                  }
                  onKeyDown={() => (closedOnKeydown.current = isLastStep)}
                >
                  {isLastStep
                    ? intl.formatMessage({
                        defaultMessage: 'Got it',
                        id: 'fzkORq3AsvX/NtMB',
                        description: 'Microguide Got it button at last step.',
                      })
                    : intl.formatMessage({
                        defaultMessage: 'Next',
                        id: '+jn6HNnzB4IDIbZu',
                        description: 'Microguide Next button.',
                      })}
                </Button>
              </Flex>
            </Flex>
          </Surface>,
          microguideContainer.current || document.body,
        )
      : null;
  },
);

(
  MicroGuideRoot as typeof MicroGuideRoot & { displayName: string }
).displayName = 'Microguide';

/**
 * The `Microguide` supports the onboarding of new users, as well as the introduction of new features to existing users.
 * It can have one or multiple steps, each of which has three optional slots: `Title`, `Visual` and `Content`.
 * @public
 */
export const Microguide = Object.assign(MicroGuideRoot, {
  Step,
  Title,
  Visual,
  Content,
});
